import { jsPDF } from 'jspdf'
import { productThemes } from '@/lib/productBranding'
import { resolveWidgetWidthPercent } from '../builderCore'
import {
  CHART_AXIS_LINE_COLOR,
  CHART_BAR_TRACK,
  CHART_GRID_COLOR,
  DONUT_COLORS,
  getSeriesColor,
} from '../chartTheme'
import { findDrilldownColumn } from '../drilldownHierarchy'
import {
  buildGaugeLegend,
  GAUGE_COLORS,
  GAUGE_DIAL,
  GAUGE_PROGRESS_GRADIENT,
  GAUGE_TONES,
  resolveGaugeBadge,
  resolveGaugeProgress,
  resolveGaugeValues,
  type GaugeBadge,
} from '../gauge'
import { mapWidgetToAgentBuilderData } from '../presentation'
import { parseInsightSections } from '../insightSections'
import {
  classifyColumns,
  deriveChartDataForDimension,
  formatColumnValue,
  formatCompactColumnValue,
  formatDimensionLabel,
  formatFieldDisplayName,
  formatFieldReferencesInText,
  formatShortDateLabel,
  formatTableCellString,
  formatTableCellValue,
  getBarAxisMin,
  pickLabelColumn,
  resolveChartDimension,
} from '../vizUtils'
import {
  aggregateLineChartDataByDate,
  buildColumnBarMax,
  buildExpansionRowKey,
  buildLineChartRows,
  buildPivotChildRows,
  buildPivotMatrix,
  formatLineSeriesName,
  getLineSeriesColor,
  inCellBarPercent,
  formatKpiCardValue,
  DEFAULT_NEGATIVE_COLOR,
  isEmptyCellValue,
  pivotRowTotal,
  resolveExpandableCellValue,
  resolveKpiCardTheme,
  resolveNumericColumns,
  shiftChartPointLabelsByYears,
  TABLE_NEGATIVE_BAR_COLOR,
  tableBarColor,
  type ExpandedTableResult,
  type WidgetPresentationState,
} from '../vizModel'
import type { WidgetSessionState } from './engine/state'
import type { PdfRowCoverage } from './pdfData'
import {
  blendRgb,
  dialArcPoints,
  dialPoint,
  donutSliceAngles,
  donutSlicePolygon,
  hexToRgb,
  mixRgb,
  monotoneCurveSegments,
  niceTicks,
  type Point,
  type Rgb,
} from './pdfGeometry'
import { describeDashboardDateRange, describeDateRangeSubtitle } from '../datePresets'
import type {
  AgentBuilderChartDataPoint,
  AgentBuilderReportData,
  DashboardDefinition,
  DashboardRuntimeState,
  DashboardTrendGranularity,
  DashboardWidget,
  DatePresetOption,
} from '../types'

// Dashboard PDF, drawn with jsPDF vector primitives (searchable text, sharp at any zoom).
// The look follows the Agent Builder viewer (mockups/dashboard.css, chartTheme.ts, Viz.tsx, GaugeViz.tsx):
// white cards with a teal accent bar, track-backed bars, smooth area lines, a semicircle gauge and
// tinted-header tables. Data shaping is shared with the screen through vizModel.ts / gauge.ts.

const WHITE: Rgb = [255, 255, 255]
const COLOR = {
  ink: hexToRgb('#1d2433'),
  heading: hexToRgb('#111827'),
  muted: hexToRgb('#667085'),
  label: hexToRgb('#475467'),
  gray500: hexToRgb('#6b7280'),
  gray700: hexToRgb('#374151'),
  line: hexToRgb('#e6eaef'),
  rowDivider: hexToRgb('#f0f2f5'),
  tableHead: hexToRgb('#f3fbfa'),
  grid: hexToRgb(CHART_GRID_COLOR),
  axisLine: hexToRgb(CHART_AXIS_LINE_COLOR),
  accent: hexToRgb(productThemes.pracIntel.primary),
  tealText: hexToRgb('#0f8f82'),
  negative: hexToRgb(TABLE_NEGATIVE_BAR_COLOR),
  emptyDash: hexToRgb('#b0b8c3'),
  barTrack: hexToRgb(CHART_BAR_TRACK.fill),
  cellBarTrack: hexToRgb('#f3f5f8'),
  legendBoxFill: hexToRgb('#f9fafb'),
  legendBoxBorder: hexToRgb('#f3f4f6'),
} as const

const FONT = 'helvetica'

const PAGE_MARGIN = 12
const PAGE_TOP = 14
const PAGE_BOTTOM = 12
const GRID_GAP = 5
const CARD_RADIUS = 2.4
const CARD_PAD_X = 5
const CARD_HEADER_MIN = 11
const CARD_BODY_TOP = 4
const CARD_BODY_BOTTOM = 5
const CARD_TITLE_SIZE = 9.6
const CARD_DESCRIPTION_SIZE = 7.6
const ACCENT_BAR_W = 0.8
const ACCENT_BAR_H = 4.4
const MAX_TABLE_COLUMNS = 12
const BAR_CHART_POINT_LIMIT = 20
/** In-cell bars are drawn at 35% opacity over their track (mockups `.cb i { opacity: 0.35 }`). */
const IN_CELL_BAR_OPACITY = 0.35

export type DashboardPdfSnapshot = {
  dashboard: DashboardDefinition
  dashboardId: string | null
  widgetSessions: Record<string, WidgetSessionState>
  runtimeState: DashboardRuntimeState
  dashboardInsightText?: string | null
  /** Catalog presets, used to print the active preset's label and resolved window. */
  datePresetOptions?: DatePresetOption[]
  datePresetsAsOf?: string | null
  /** Client (workspace) name, printed as the eyebrow above the title like the viewer header. */
  clientName?: string | null
  /** Card-local screen state: trend granularity and expanded drilldown rows, per widget. */
  widgetPresentation?: Record<string, WidgetPresentationState>
  /** Loaded vs total rows per table widget; a table with loaded < total notes that it is partial. */
  rowCoverage?: Record<string, PdfRowCoverage>
  capturedAt: string
}

type PdfCursor = {
  pdf: jsPDF
  y: number
}

type Rect = {
  x: number
  y: number
  w: number
  h: number
}

type MeasuredWidget = {
  widget: DashboardWidget
  data: AgentBuilderReportData
  presentation: WidgetPresentationState
  badge: GaugeBadge | null
  widthPercent: number
  /** Card height of a visual widget (0 for tables, which paginate). */
  height: number
  bodyHeight: number
  isTable: boolean
}

// ---------------------------------------------------------------------------
// Primitives

function setTextColor(pdf: jsPDF, color: Rgb) {
  pdf.setTextColor(color[0], color[1], color[2])
}

function setFillColor(pdf: jsPDF, color: Rgb) {
  pdf.setFillColor(color[0], color[1], color[2])
}

function setDrawColor(pdf: jsPDF, color: Rgb) {
  pdf.setDrawColor(color[0], color[1], color[2])
}

function setFont(pdf: jsPDF, size: number, style: 'normal' | 'bold' = 'normal') {
  pdf.setFont(FONT, style)
  pdf.setFontSize(size)
}

function getPageWidth(pdf: jsPDF) {
  return pdf.internal.pageSize.getWidth()
}

function getPageHeight(pdf: jsPDF) {
  return pdf.internal.pageSize.getHeight()
}

function getContentWidth(pdf: jsPDF) {
  return getPageWidth(pdf) - PAGE_MARGIN * 2
}

function getPageBottom(pdf: jsPDF) {
  return getPageHeight(pdf) - PAGE_BOTTOM - 7
}

function addPage(cursor: PdfCursor) {
  cursor.pdf.addPage('a4', 'portrait')
  cursor.y = PAGE_TOP
}

function ensureSpace(cursor: PdfCursor, height: number) {
  if (cursor.y + height <= getPageBottom(cursor.pdf)) return
  addPage(cursor)
}

function getLineHeight(fontSize: number, lineGap = 1.25) {
  return fontSize * 0.35 + lineGap
}

/** Distance from a text line's top to its alphabetic baseline, in mm. */
function getAscent(fontSize: number) {
  return fontSize * 0.3528 * 0.78
}

function getWrappedLines(pdf: jsPDF, text: string, width: number, fontSize: number, fontStyle: 'normal' | 'bold' = 'normal') {
  setFont(pdf, fontSize, fontStyle)
  return pdf.splitTextToSize(text, Math.max(8, width)) as string[]
}

function measureText(
  pdf: jsPDF,
  text: string | null | undefined,
  width: number,
  fontSize: number,
  fontStyle: 'normal' | 'bold' = 'normal',
  lineGap = 1.25,
) {
  if (!text?.trim()) return 0
  return getWrappedLines(pdf, text, width, fontSize, fontStyle).length * getLineHeight(fontSize, lineGap)
}

/** Draws wrapped text whose first line's top is at `top`; returns the y below the last line. */
function drawWrappedText(
  pdf: jsPDF,
  text: string,
  x: number,
  top: number,
  width: number,
  options: { fontSize?: number; color?: Rgb; fontStyle?: 'normal' | 'bold'; lineGap?: number } = {},
) {
  const fontSize = options.fontSize ?? 8
  const lineHeight = getLineHeight(fontSize, options.lineGap ?? 1.25)
  const lines = getWrappedLines(pdf, text, width, fontSize, options.fontStyle ?? 'normal')
  setTextColor(pdf, options.color ?? COLOR.ink)
  lines.forEach((line, index) => {
    pdf.text(line, x, top + getAscent(fontSize) + index * lineHeight)
  })
  return top + lines.length * lineHeight
}

/** One line of text cut to `width` with an ellipsis (uses the current font). */
function fitText(pdf: jsPDF, value: string, width: number) {
  const text = value.replace(/\s+/g, ' ').trim()
  if (width <= 0) return ''
  if (pdf.getTextWidth(text) <= width) return text
  const ellipsis = '...'
  let low = 0
  let high = text.length
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    if (pdf.getTextWidth(text.slice(0, mid).trimEnd() + ellipsis) <= width) low = mid
    else high = mid - 1
  }
  return low > 0 ? text.slice(0, low).trimEnd() + ellipsis : ''
}

function roundedRect(pdf: jsPDF, rect: Rect, radius: number, style: 'F' | 'S' | 'FD') {
  const r = Math.max(0, Math.min(radius, rect.w / 2, rect.h / 2))
  if (r <= 0.01) pdf.rect(rect.x, rect.y, rect.w, rect.h, style)
  else pdf.roundedRect(rect.x, rect.y, rect.w, rect.h, r, r, style)
}

/** A bar whose far end (right for positive values) is rounded and whose base is square. */
function drawEndRoundedBar(pdf: jsPDF, rect: Rect, radius: number, roundRight: boolean, color: Rgb) {
  if (rect.w <= 0 || rect.h <= 0) return
  setFillColor(pdf, color)
  const r = Math.min(radius, rect.w / 2, rect.h / 2)
  roundedRect(pdf, rect, r, 'F')
  if (r > 0.01) {
    pdf.rect(roundRight ? rect.x : rect.x + rect.w - r, rect.y, r, rect.h, 'F')
  }
}

/** Many clustered bars in a narrow slot narrow their gaps first, so every bar keeps some thickness. */
export function clusterBarSizes(slot: number, seriesCount: number, maxThickness: number) {
  const gaps = Math.max(0, seriesCount - 1)
  const barGap = gaps > 0 ? Math.min(0.5, slot / 2 / gaps) : 0
  return { barGap, thickness: Math.min(maxThickness, (slot - barGap * gaps) / Math.max(1, seriesCount)) }
}

function drawEndRoundedColumn(pdf: jsPDF, rect: Rect, radius: number, roundTop: boolean, color: Rgb) {
  if (rect.w <= 0 || rect.h <= 0) return
  setFillColor(pdf, color)
  const r = Math.min(radius, rect.w / 2, rect.h / 2)
  roundedRect(pdf, rect, r, 'F')
  if (r > 0.01) {
    pdf.rect(rect.x, roundTop ? rect.y + rect.h - r : rect.y, rect.w, r, 'F')
  }
}

function fillPolygon(pdf: jsPDF, points: Point[], color: Rgb) {
  const [first, ...rest] = points
  if (!first || rest.length < 2) return
  setFillColor(pdf, color)
  pdf.moveTo(first.x, first.y)
  rest.forEach((point) => pdf.lineTo(point.x, point.y))
  pdf.close()
  pdf.fill()
}

function strokePolyline(pdf: jsPDF, points: Point[], color: Rgb, width: number, cap: 'round' | 'butt' = 'round') {
  const [first, ...rest] = points
  if (!first || rest.length === 0) return
  setDrawColor(pdf, color)
  pdf.setLineWidth(width)
  pdf.setLineCap(cap)
  pdf.setLineJoin('round')
  pdf.moveTo(first.x, first.y)
  rest.forEach((point) => pdf.lineTo(point.x, point.y))
  pdf.stroke()
  pdf.setLineCap('butt')
  pdf.setLineJoin('miter')
  pdf.setLineWidth(0.2)
}

function drawDownChevron(pdf: jsPDF, x: number, y: number, color: Rgb) {
  strokePolyline(pdf, [{ x: x - 0.9, y: y - 0.45 }, { x, y: y + 0.45 }, { x: x + 0.9, y: y - 0.45 }], color, 0.3)
}

function drawNoData(pdf: jsPDF, body: Rect, message: string) {
  drawWrappedText(pdf, message, body.x, body.y + 2, body.w, { fontSize: 8, color: COLOR.muted })
}

// ---------------------------------------------------------------------------
// Cards

type CardHeaderLayout = {
  titleLines: string[]
  bandHeight: number
  descriptionLines: string[]
  /** Distance from the card top to the body top. */
  bodyOffset: number
  badge: GaugeBadge | null
  badgeWidth: number
}

const TITLE_LINE_HEIGHT = getLineHeight(CARD_TITLE_SIZE, 1.1)
const DESCRIPTION_LINE_HEIGHT = getLineHeight(CARD_DESCRIPTION_SIZE, 1.1)
const BADGE_FONT_SIZE = 6.8
const BADGE_HEIGHT = 5.4

function measureBadgeWidth(pdf: jsPDF, badge: GaugeBadge | null) {
  if (!badge) return 0
  setFont(pdf, BADGE_FONT_SIZE, 'bold')
  return pdf.getTextWidth(badge.text) + 5
}

function layoutCardHeader(pdf: jsPDF, title: string, description: string | null | undefined, width: number, badge: GaugeBadge | null = null): CardHeaderLayout {
  const badgeWidth = measureBadgeWidth(pdf, badge)
  const titleWidth = width - CARD_PAD_X * 2 - ACCENT_BAR_W - 2.6 - (badgeWidth > 0 ? badgeWidth + 3 : 0)
  const titleLines = getWrappedLines(pdf, title || 'Widget', titleWidth, CARD_TITLE_SIZE, 'bold')
  const bandHeight = Math.max(CARD_HEADER_MIN, titleLines.length * TITLE_LINE_HEIGHT + 5.6)
  const descriptionLines = description?.trim()
    ? getWrappedLines(pdf, description, width - CARD_PAD_X * 2, CARD_DESCRIPTION_SIZE)
    : []
  const descriptionHeight = descriptionLines.length > 0 ? descriptionLines.length * DESCRIPTION_LINE_HEIGHT + 3 : 0
  return {
    titleLines,
    bandHeight,
    descriptionLines,
    bodyOffset: bandHeight + CARD_BODY_TOP + descriptionHeight,
    badge,
    badgeWidth,
  }
}

/** Card chrome (mockups `.card` / `.card-head`): white card, header band with an accent bar, title, badge and description. Returns the body top. */
function drawCardFrame(pdf: jsPDF, rect: Rect, layout: CardHeaderLayout) {
  setFillColor(pdf, WHITE)
  setDrawColor(pdf, COLOR.line)
  pdf.setLineWidth(0.25)
  roundedRect(pdf, rect, CARD_RADIUS, 'FD')
  pdf.setLineWidth(0.2)

  const bandBottom = rect.y + layout.bandHeight
  setDrawColor(pdf, COLOR.line)
  pdf.line(rect.x, bandBottom, rect.x + rect.w, bandBottom)

  const titleBlockHeight = layout.titleLines.length * TITLE_LINE_HEIGHT - 1.1
  const titleTop = rect.y + (layout.bandHeight - titleBlockHeight) / 2
  const accentX = rect.x + CARD_PAD_X
  const accentY = titleTop + (Math.min(titleBlockHeight, ACCENT_BAR_H * 1.6) - ACCENT_BAR_H) / 2
  setFillColor(pdf, COLOR.accent)
  pdf.roundedRect(accentX, accentY, ACCENT_BAR_W, ACCENT_BAR_H, ACCENT_BAR_W / 2, ACCENT_BAR_W / 2, 'F')

  setFont(pdf, CARD_TITLE_SIZE, 'bold')
  setTextColor(pdf, COLOR.ink)
  layout.titleLines.forEach((line, index) => {
    pdf.text(line, accentX + ACCENT_BAR_W + 2.6, titleTop + getAscent(CARD_TITLE_SIZE) + index * TITLE_LINE_HEIGHT)
  })

  if (layout.badge && layout.badgeWidth > 0) {
    const tone = GAUGE_TONES[layout.badge.tone]
    const badgeRect = {
      x: rect.x + rect.w - CARD_PAD_X - layout.badgeWidth,
      y: rect.y + (layout.bandHeight - BADGE_HEIGHT) / 2,
      w: layout.badgeWidth,
      h: BADGE_HEIGHT,
    }
    setFillColor(pdf, hexToRgb(tone.badgeBackground))
    roundedRect(pdf, badgeRect, 1.6, 'F')
    setFont(pdf, BADGE_FONT_SIZE, 'bold')
    setTextColor(pdf, hexToRgb(tone.badgeText))
    pdf.text(layout.badge.text, badgeRect.x + badgeRect.w / 2, badgeRect.y + BADGE_HEIGHT / 2, { align: 'center', baseline: 'middle' })
  }

  let y = bandBottom + CARD_BODY_TOP
  if (layout.descriptionLines.length > 0) {
    setFont(pdf, CARD_DESCRIPTION_SIZE)
    setTextColor(pdf, COLOR.muted)
    layout.descriptionLines.forEach((line, index) => {
      pdf.text(line, rect.x + CARD_PAD_X, y + getAscent(CARD_DESCRIPTION_SIZE) + index * DESCRIPTION_LINE_HEIGHT)
    })
    y += layout.descriptionLines.length * DESCRIPTION_LINE_HEIGHT + 3
  }
  return y
}

// ---------------------------------------------------------------------------
// Page header and dashboard context

function getFilterSummary(snapshot: DashboardPdfSnapshot) {
  const { runtimeState } = snapshot
  const entries = Object.entries(runtimeState.slicerValues).flatMap(([field, rawValue]) => {
    const values = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : []
    const normalized = values
      .map((value) => String(value ?? '').trim())
      .filter((value) => value && value.toLowerCase() !== 'all')
    return normalized.length > 0 ? [`${formatFieldDisplayName(field)}: ${normalized.join(', ')}`] : []
  })
  const { dateRange } = runtimeState
  if (dateRange.mode === 'preset') {
    const description = describeDashboardDateRange(dateRange, snapshot.datePresetOptions, snapshot.datePresetsAsOf)
    if (description) entries.push(`Date range: ${description}`)
  } else if (dateRange.mode === 'custom') {
    if (dateRange.from) entries.push(`From: ${dateRange.from}`)
    if (dateRange.to) entries.push(`To: ${dateRange.to}`)
  }
  return entries
}

/** Viewer page header: client eyebrow, title, summary and capture time. */
function drawHeader(cursor: PdfCursor, snapshot: DashboardPdfSnapshot) {
  const pdf = cursor.pdf
  const x = PAGE_MARGIN + 1
  const width = getContentWidth(pdf) - 2
  let y = cursor.y

  const eyebrow = (snapshot.clientName?.trim() || 'Dashboard Insights').toUpperCase()
  setFont(pdf, 8, 'bold')
  setTextColor(pdf, COLOR.tealText)
  pdf.text(fitText(pdf, eyebrow, width), x, y + getAscent(8), { charSpace: 0.35 })
  y += 5.2

  setFont(pdf, 18, 'bold')
  setTextColor(pdf, COLOR.heading)
  pdf.text(fitText(pdf, snapshot.dashboard.title || 'Dashboard', width), x, y + getAscent(18))
  y += 9.4

  y = drawWrappedText(pdf, snapshot.dashboard.summary || 'No summary provided.', x, y, width, {
    fontSize: 8.6,
    color: COLOR.muted,
    lineGap: 1.4,
  }) + 1.6

  setFont(pdf, 7.2)
  setTextColor(pdf, COLOR.muted)
  pdf.text(`Captured: ${new Date(snapshot.capturedAt).toLocaleString()}`, x, y + getAscent(7.2))
  y += 4.4

  cursor.y = y + GRID_GAP
}

const INSIGHT_HEADING_SIZE = 7
const INSIGHT_BULLET_SIZE = 8.2

function getInsightGroups(insightText: string | null | undefined) {
  const sections = parseInsightSections(insightText)
  return [
    { heading: sections.hasSections ? 'Key findings' : null, items: sections.keyFindings },
    { heading: 'Recommendations', items: sections.recommendations },
  ].filter((group) => group.items.length > 0)
}

/** A titled card with free-form body content measured and drawn by the caller. */
function drawContextCard(cursor: PdfCursor, title: string, bodyHeight: number, drawBody: (body: Rect) => void) {
  const pdf = cursor.pdf
  const width = getContentWidth(pdf)
  const layout = layoutCardHeader(pdf, title, null, width)
  const height = layout.bodyOffset + bodyHeight + CARD_BODY_BOTTOM
  ensureSpace(cursor, height)
  const rect = { x: PAGE_MARGIN, y: cursor.y, w: width, h: height }
  const bodyY = drawCardFrame(pdf, rect, layout)
  drawBody({ x: rect.x + CARD_PAD_X, y: bodyY, w: rect.w - CARD_PAD_X * 2, h: bodyHeight })
  cursor.y = rect.y + rect.h + GRID_GAP
}

/** Applied filters, then the dashboard insights (same content and skip rules as before, in the new card style). */
function drawDashboardContext(cursor: PdfCursor, snapshot: DashboardPdfSnapshot) {
  const pdf = cursor.pdf
  const filters = getFilterSummary(snapshot)
  const insightGroups = getInsightGroups(snapshot.dashboardInsightText)
  const innerW = getContentWidth(pdf) - CARD_PAD_X * 2

  if (filters.length > 0) {
    const text = filters.join('  |  ')
    drawContextCard(cursor, 'Filters', measureText(pdf, text, innerW, 8), (body) => {
      drawWrappedText(pdf, text, body.x, body.y, body.w, { fontSize: 8, color: COLOR.label })
    })
  }

  if (insightGroups.length > 0) {
    const bulletW = innerW - 4.5
    const bodyHeight = insightGroups.reduce((sum, group, groupIndex) => (
      sum
      + (groupIndex > 0 ? 3.5 : 0)
      + (group.heading ? 5 : 0)
      + group.items.reduce((itemSum, item) => itemSum + measureText(pdf, item, bulletW, INSIGHT_BULLET_SIZE, 'normal', 1.45) + 2, 0)
    ), 0)

    drawContextCard(cursor, 'Dashboard Insights', bodyHeight, (body) => {
      let y = body.y
      insightGroups.forEach((group, groupIndex) => {
        if (groupIndex > 0) y += 3.5
        if (group.heading) {
          setFont(pdf, INSIGHT_HEADING_SIZE, 'bold')
          setTextColor(pdf, COLOR.gray500)
          pdf.text(group.heading.toUpperCase(), body.x, y + getAscent(INSIGHT_HEADING_SIZE), { charSpace: 0.2 })
          y += 5
        }
        group.items.forEach((item) => {
          setFillColor(pdf, COLOR.gray700)
          pdf.circle(body.x + 1.3, y + getAscent(INSIGHT_BULLET_SIZE) - 0.9, 0.55, 'F')
          y = drawWrappedText(pdf, item, body.x + 4.5, y, bulletW, {
            fontSize: INSIGHT_BULLET_SIZE,
            color: COLOR.gray700,
            lineGap: 1.45,
          }) + 2
        })
      })
    })
  }
}

// ---------------------------------------------------------------------------
// Widget data

function isTableWidget(widget: DashboardWidget) {
  return widget.type === 'table' || widget.type === 'dataBarTable' || widget.type === 'pivotTable'
}

function toWidgetData(widget: DashboardWidget, snapshot: DashboardPdfSnapshot) {
  const session = snapshot.widgetSessions[widget.id]
  if (!session) return null
  return mapWidgetToAgentBuilderData(widget, {
    status: session.status === 'error' && session.data.rows.length === 0 ? 'error' : 'live',
    stage: 'execute_dax_query',
    rows: session.data.rows,
    columns: session.data.columns,
    chartData: session.data.chartData,
    comparisonChartData: session.data.comparisonChartData,
    kpiCards: session.data.kpiCards,
    daxQuery: session.data.daxQuery,
    error: session.error,
    hasPaginationMetadata: session.data.hasPaginationMetadata,
    totalRows: session.data.totalRows,
    page: session.data.page,
    pageSize: session.data.pageSize,
    sortColumn: session.data.sortColumn,
    sortDirection: session.data.sortDirection,
    groupByDimension: session.query.groupByDimension ?? session.data.groupByDimension,
    axisMinValue: session.data.axisMinValue,
    axisMaxValue: session.data.axisMaxValue,
    axisMinByMeasure: session.data.axisMinByMeasure,
    axisMaxByMeasure: session.data.axisMaxByMeasure,
    columnFormats: session.data.columnFormats,
    lastUpdatedAt: session.data.lastUpdatedAt,
  })
}

function getWidgetWidthPercent(widget: DashboardWidget) {
  if (isTableWidget(widget)) return 100
  return Math.min(100, Math.max(25, resolveWidgetWidthPercent(widget)))
}

function getWidgetBoxWidth(contentWidth: number, widthPercent: number) {
  if (widthPercent >= 100) return contentWidth
  return Math.max(42, contentWidth * (widthPercent / 100) - GRID_GAP * (1 - widthPercent / 100))
}

function getVisualBodyMaxHeight(pdf: jsPDF, bodyOffset: number) {
  return getPageBottom(pdf) - PAGE_TOP - bodyOffset - CARD_BODY_BOTTOM
}

function getActiveMeasure(data: AgentBuilderReportData) {
  const { measureCols } = classifyColumns(data.columns, data.rows)
  return measureCols[0] ?? data.chartData[0]?.series?.[0]?.name ?? 'Value'
}

// ---------------------------------------------------------------------------
// KPI tiles (Viz.tsx KpiViz)

const KPI_TILE_H = 21
const KPI_TILE_GAP = 4

function getKpiColumns(count: number, width: number) {
  if (count <= 1) return 1
  if (count === 2) return 2
  if (count === 3) return width >= 90 ? 3 : 1
  return width >= 120 ? 4 : 2
}

function measureKpis(data: AgentBuilderReportData, width: number) {
  const count = data.kpiCards.length
  const cols = getKpiColumns(count, width)
  const rows = Math.ceil(count / cols)
  return rows * KPI_TILE_H + (rows - 1) * KPI_TILE_GAP
}

function drawKpis(pdf: jsPDF, body: Rect, data: AgentBuilderReportData) {
  const cards = data.kpiCards
  const cols = getKpiColumns(cards.length, body.w)
  const tileW = (body.w - KPI_TILE_GAP * (cols - 1)) / cols

  cards.forEach((card, index) => {
    const x = body.x + (index % cols) * (tileW + KPI_TILE_GAP)
    const y = body.y + Math.floor(index / cols) * (KPI_TILE_H + KPI_TILE_GAP)
    const theme = resolveKpiCardTheme(card, index)
    const fill = hexToRgb(theme.border)
    // A negative value with format.negativeColor sits on a white tile with the accent as its border, as on screen.
    const negative = theme.negative
    const soft = negative ? hexToRgb(theme.captionColor) : blendRgb(WHITE, fill, 0.78)
    setFillColor(pdf, negative ? WHITE : fill)
    roundedRect(pdf, { x, y, w: tileW, h: KPI_TILE_H }, 3.2, 'F')
    if (negative) {
      setDrawColor(pdf, fill)
      pdf.setLineWidth(0.4)
      roundedRect(pdf, { x, y, w: tileW, h: KPI_TILE_H }, 3.2, 'S')
    }

    const innerW = tileW - 7
    const value = formatKpiCardValue(card, data.columnFormats)
    setFont(pdf, 13.5, 'bold')
    setTextColor(pdf, negative ? hexToRgb(/^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(theme.valueColor) ? theme.valueColor : DEFAULT_NEGATIVE_COLOR) : WHITE)
    const valueText = fitText(pdf, value, innerW)
    pdf.text(valueText, x + 3.5, y + 9)
    if (card.unit) {
      const valueWidth = pdf.getTextWidth(valueText)
      setFont(pdf, 7.5)
      setTextColor(pdf, soft)
      pdf.text(fitText(pdf, card.unit, innerW - valueWidth - 1), x + 3.5 + valueWidth + 1, y + 9)
    }

    const caption = card.displayLabel ?? (card.measureLabel ? formatFieldReferencesInText(card.measureLabel) : '')
    setFont(pdf, 6.8, 'bold')
    setTextColor(pdf, soft)
    if (caption) pdf.text(fitText(pdf, caption, innerW), x + 3.5, y + 14.8)
  })
}

// ---------------------------------------------------------------------------
// Bar charts (Viz.tsx BarViz / GroupedBarViz)

const BAR_ROW_H = 7
const BAR_AXIS_H = 6
const BAR_LEGEND_H = 6
const BAR_RADIUS = 1.1
const COLUMN_CHART_H = 62
const COLUMN_VALUE_LABEL_H = 4
const COLUMN_LABEL_ANGLE = 35
const COLUMN_LABEL_MAX_W = 26
const COLUMN_VALUE_LABEL_MIN_FONT = 4.5

function getGroupedSeriesNames(points: AgentBuilderChartDataPoint[]) {
  return points.find((point) => point.series && point.series.length > 0)?.series?.map((series) => series.name) ?? []
}

function measureBarChart(data: AgentBuilderReportData, grouped: boolean, orientation?: DashboardWidget['orientation']) {
  const points = data.chartData.slice(0, BAR_CHART_POINT_LIMIT)
  if (points.length === 0) return 12
  if (orientation === 'vertical') return (grouped ? BAR_LEGEND_H : 0) + COLUMN_CHART_H
  const seriesCount = grouped ? Math.max(1, getGroupedSeriesNames(points).length) : 1
  const rowH = grouped ? Math.max(BAR_ROW_H, seriesCount * 2.6 + 2.6) : BAR_ROW_H
  return (grouped ? BAR_LEGEND_H : 0) + points.length * rowH + BAR_AXIS_H + 1
}

function drawLegend(pdf: jsPDF, right: number, y: number, entries: Array<{ label: string; color: Rgb }>) {
  setFont(pdf, 6.8)
  let x = right
  ;[...entries].reverse().forEach((entry) => {
    const text = fitText(pdf, entry.label, 48)
    const textW = pdf.getTextWidth(text)
    x -= textW
    setTextColor(pdf, COLOR.label)
    pdf.text(text, x, y + 1.1)
    setFillColor(pdf, entry.color)
    pdf.circle(x - 2, y, 0.95, 'F')
    x -= 6
  })
}

function buildBarModel(data: AgentBuilderReportData, grouped: boolean) {
  const points = data.chartData.slice(0, BAR_CHART_POINT_LIMIT)
  const { measureCols, dimensionCols } = classifyColumns(data.columns, data.rows)
  const dimension = resolveChartDimension(data.dimensions, dimensionCols, data.rows)
  const seriesNames = grouped ? getGroupedSeriesNames(points) : []
  const activeMeasure = measureCols[0] ?? data.chartData[0]?.series?.[0]?.name ?? 'Value'
  const axisColumn = grouped ? (measureCols[0] ?? seriesNames[0]) : activeMeasure
  const series = grouped
    ? seriesNames.map((name, index) => ({ name, color: hexToRgb(getSeriesColor(index)) }))
    : [{ name: 'value', color: COLOR.accent }]
  const valueOf = (point: AgentBuilderChartDataPoint, name: string) => (grouped
    ? point.series?.find((item) => item.name === name)?.value
    : point.value)
  const formatValueLabel = (value: number, name: string) => formatCompactColumnValue(value, grouped ? name : activeMeasure, data.columnFormats)
  const formatTick = (value: number) => formatCompactColumnValue(value, axisColumn, data.columnFormats)
  const categoryLabels = points.map((point) => formatDimensionLabel(point.label, dimension, data.columnFormats))

  const values = points.flatMap((point) => (grouped ? point.series?.map((item) => item.value) ?? [point.value] : [point.value]))
  const ticks = niceTicks(getBarAxisMin(values), Math.max(0, ...values.filter(Number.isFinite)), 5, false)
  const min = ticks[0] ?? 0
  const max = ticks[ticks.length - 1] ?? 1
  return { points, series, valueOf, formatValueLabel, formatTick, categoryLabels, ticks, min, max, span: max - min || 1, grouped }
}

type BarModel = ReturnType<typeof buildBarModel>

function drawBarChart(pdf: jsPDF, body: Rect, data: AgentBuilderReportData, grouped: boolean, orientation?: DashboardWidget['orientation']) {
  const model = buildBarModel(data, grouped)
  if (model.points.length === 0) {
    drawNoData(pdf, body, 'No chart data available.')
    return
  }

  let top = body.y
  if (grouped && model.series.length > 0) {
    drawLegend(pdf, body.x + body.w, top + 1.8, model.series.map((item) => ({
      label: data.columnLabels?.[item.name] ?? formatFieldDisplayName(item.name),
      color: item.color,
    })))
    top += BAR_LEGEND_H
  }

  const plot = { x: body.x, y: top, w: body.w, h: body.y + body.h - top }
  if (orientation === 'vertical') drawColumns(pdf, plot, model)
  else drawBarsAcross(pdf, plot, model)
}

function drawBarsAcross(pdf: jsPDF, plot: Rect, model: BarModel) {
  const { points, series, valueOf, formatValueLabel, formatTick, categoryLabels, ticks, min, max, span, grouped } = model
  const top = plot.y

  // Category labels on the left (right-aligned to the plot), value labels to the right of each bar.
  setFont(pdf, 7)
  const labelW = Math.min(plot.w * 0.34, Math.max(18, ...categoryLabels.map((label) => pdf.getTextWidth(label) + 3)))
  setFont(pdf, grouped ? 6 : 6.8, 'bold')
  const valueLabelW = Math.max(10, ...points.flatMap((point) => series.map((item) => {
    const value = valueOf(point, item.name)
    return typeof value === 'number' ? pdf.getTextWidth(formatValueLabel(value, item.name)) + 2.5 : 0
  })))
  const plotX = plot.x + labelW
  const plotW = Math.max(10, plot.w - labelW - valueLabelW)
  const rowH = (plot.h - BAR_AXIS_H - 1) / points.length
  const plotH = rowH * points.length
  const toX = (value: number) => plotX + ((value - min) / span) * plotW
  const zeroX = toX(Math.max(min, Math.min(0, max)))

  // Vertical grid and value-axis ticks.
  setDrawColor(pdf, COLOR.grid)
  pdf.setLineWidth(0.2)
  setFont(pdf, 6)
  setTextColor(pdf, COLOR.muted)
  ticks.forEach((tick) => {
    const x = toX(tick)
    pdf.line(x, top, x, top + plotH)
    pdf.text(formatTick(tick), x, top + plotH + 4, { align: 'center' })
  })

  const gapRatio = grouped ? 0.24 : 0.28
  points.forEach((point, index) => {
    const rowTop = top + index * rowH
    const slot = rowH * (1 - gapRatio)
    const center = rowTop + rowH / 2

    setFont(pdf, 7)
    setTextColor(pdf, COLOR.ink)
    pdf.text(fitText(pdf, categoryLabels[index] ?? '', labelW - 3), plotX - 2.4, center + 1, { align: 'right' })

    const { barGap, thickness } = grouped ? clusterBarSizes(slot, series.length, 2.4) : { barGap: 0, thickness: Math.min(3.6, slot) }
    const groupH = thickness * series.length + barGap * (series.length - 1)

    if (!grouped) {
      // Single series: a full-width track behind the bar (CHART_BAR_TRACK).
      setFillColor(pdf, COLOR.barTrack)
      roundedRect(pdf, { x: plotX, y: center - thickness / 2, w: plotW, h: thickness }, BAR_RADIUS, 'F')
    }

    series.forEach((item, seriesIndex) => {
      const value = valueOf(point, item.name)
      if (typeof value !== 'number' || !Number.isFinite(value)) return
      const y = center - groupH / 2 + seriesIndex * (thickness + barGap)
      const end = toX(value)
      const x = Math.min(zeroX, end)
      const w = Math.max(0.6, Math.abs(end - zeroX))
      drawEndRoundedBar(pdf, { x, y, w, h: thickness }, BAR_RADIUS, value >= 0, item.color)

      setFont(pdf, grouped ? 6 : 6.8, 'bold')
      setTextColor(pdf, COLOR.ink)
      pdf.text(formatValueLabel(value, item.name), Math.max(zeroX, end) + 1.3, y + thickness / 2 + (grouped ? 0.8 : 0.95))
    })
  })
}

function drawColumns(pdf: jsPDF, plot: Rect, model: BarModel) {
  const { points, series, valueOf, formatValueLabel, formatTick, categoryLabels, ticks, min, max, span, grouped } = model

  setFont(pdf, 6)
  const tickLabels = ticks.map(formatTick)
  const axisW = Math.max(8, ...tickLabels.map((label) => pdf.getTextWidth(label) + 2.5))
  const plotX = plot.x + axisW
  const plotW = Math.max(10, plot.w - axisW)
  const slotW = plotW / points.length

  // As on screen, labels that don't fit under their column are angled.
  setFont(pdf, 7)
  const angled = categoryLabels.some((label) => pdf.getTextWidth(label) > slotW - 1.5)
  const radians = (COLUMN_LABEL_ANGLE * Math.PI) / 180
  // An angled label ends under its column and starts to its left, so the first ones are cut to stay in the widget.
  const shown = categoryLabels.map((label, index) => {
    const center = plotX + slotW * (index + 0.5)
    return fitText(pdf, label, angled ? Math.min(COLUMN_LABEL_MAX_W, (center - plot.x) / Math.cos(radians)) : slotW - 1.5)
  })
  const labelH = angled ? Math.min(16, Math.max(0, ...shown.map((label) => pdf.getTextWidth(label))) * Math.sin(radians) + 4) : 5
  const plotTop = plot.y + COLUMN_VALUE_LABEL_H
  const plotBottom = plot.y + plot.h - labelH
  const plotH = Math.max(10, plotBottom - plotTop)
  const toY = (value: number) => plotBottom - ((value - min) / span) * plotH
  const zeroY = toY(Math.max(min, Math.min(0, max)))

  // Horizontal grid and value-axis ticks.
  setDrawColor(pdf, COLOR.grid)
  pdf.setLineWidth(0.2)
  setFont(pdf, 6)
  setTextColor(pdf, COLOR.muted)
  ticks.forEach((tick, index) => {
    const y = toY(tick)
    pdf.line(plotX, y, plotX + plotW, y)
    pdf.text(tickLabels[index] ?? '', plotX - 1.5, y + 1, { align: 'right' })
  })

  const gapRatio = grouped ? 0.24 : 0.28
  points.forEach((point, index) => {
    const center = plotX + slotW * (index + 0.5)
    const slot = slotW * (1 - gapRatio)
    const { barGap, thickness } = grouped ? clusterBarSizes(slot, series.length, 5) : { barGap: 0, thickness: Math.min(7, slot) }
    const groupW = thickness * series.length + barGap * (series.length - 1)

    if (!grouped) {
      setFillColor(pdf, COLOR.barTrack)
      roundedRect(pdf, { x: center - thickness / 2, y: plotTop, w: thickness, h: plotH }, BAR_RADIUS, 'F')
    }

    series.forEach((item, seriesIndex) => {
      const value = valueOf(point, item.name)
      if (typeof value !== 'number' || !Number.isFinite(value)) return
      const x = center - groupW / 2 + seriesIndex * (thickness + barGap)
      const end = toY(value)
      const h = Math.max(0.6, Math.abs(end - zeroY))
      drawEndRoundedColumn(pdf, { x, y: Math.min(zeroY, end), w: thickness, h }, BAR_RADIUS, value >= 0, item.color)

      // A label wider than the room beside its neighbour's is shrunk; a clustered one that still doesn't fit is left out, as on screen.
      const valueLabel = formatValueLabel(value, item.name)
      const fontSize = grouped ? 5.5 : 6.8
      const room = grouped ? thickness + barGap : slotW - 0.8
      setFont(pdf, fontSize, 'bold')
      const fitted = (fontSize * room) / pdf.getTextWidth(valueLabel)
      if (grouped && fitted < COLUMN_VALUE_LABEL_MIN_FONT) return
      setFont(pdf, Math.max(COLUMN_VALUE_LABEL_MIN_FONT, Math.min(fontSize, fitted)), 'bold')
      setTextColor(pdf, COLOR.ink)
      pdf.text(valueLabel, x + thickness / 2, value >= 0 ? end - 1.2 : end + 3, { align: 'center' })
    })

    setFont(pdf, 7)
    setTextColor(pdf, COLOR.ink)
    const label = shown[index] ?? ''
    if (angled) {
      // Rotated about its start, so it starts down and left and ends under the column.
      const width = pdf.getTextWidth(label)
      pdf.text(label, center - width * Math.cos(radians), plotBottom + 2.5 + width * Math.sin(radians), { angle: COLUMN_LABEL_ANGLE })
    } else {
      pdf.text(label, center, plotBottom + 3.5, { align: 'center' })
    }
  })
}

// ---------------------------------------------------------------------------
// Line chart (Viz.tsx LineViz)

const LINE_CHART_H = 62
const LINE_LEGEND_H = 6
const LINE_X_AXIS_H = 6.5

function buildLineModel(data: AgentBuilderReportData, granularity: DashboardTrendGranularity) {
  const activeMeasure = getActiveMeasure(data)
  const sorted = aggregateLineChartDataByDate(data.chartData, granularity)
  const comparison = aggregateLineChartDataByDate(shiftChartPointLabelsByYears(data.comparisonChartData ?? [], 1), granularity)
  // Every point, as on screen.
  const { rows, seriesKeys } = buildLineChartRows(sorted, comparison, activeMeasure, data.comparePreviousYear === true)
  return { activeMeasure, rows, seriesKeys, isEmpty: sorted.length === 0 }
}

function resolveTrendGranularity(measured: Pick<MeasuredWidget, 'widget' | 'presentation'>): DashboardTrendGranularity {
  return measured.presentation.trendGranularity ?? measured.widget.trendGranularity ?? 'daily'
}

function measureLineChart(data: AgentBuilderReportData, granularity: DashboardTrendGranularity) {
  const model = buildLineModel(data, granularity)
  if (model.isEmpty) return 12
  return LINE_CHART_H + (model.seriesKeys.length > 1 ? LINE_LEGEND_H : 0)
}

function drawLineChart(pdf: jsPDF, body: Rect, data: AgentBuilderReportData, granularity: DashboardTrendGranularity) {
  const model = buildLineModel(data, granularity)
  if (model.isEmpty) {
    drawNoData(pdf, body, 'No chart data available.')
    return
  }
  const { rows, seriesKeys, activeMeasure } = model
  const currentKeys = seriesKeys.filter((key) => !key.startsWith('previous:'))
  const previousKeys = seriesKeys.filter((key) => key.startsWith('previous:'))

  let top = body.y
  if (seriesKeys.length > 1) {
    drawLegend(pdf, body.x + body.w, top + 1.8, seriesKeys.map((key) => ({
      label: formatLineSeriesName(key),
      color: hexToRgb(getLineSeriesColor(key, seriesKeys)),
    })))
    top += LINE_LEGEND_H
  }

  // The Y axis starts at zero unless the data goes negative; otherwise it follows the data.
  const allValues = rows.flatMap((row) => seriesKeys.map((key) => row[key]).filter((value): value is number => typeof value === 'number' && Number.isFinite(value)))
  const lowest = Math.min(0, ...allValues)
  const highest = Math.max(...allValues, lowest < 0 ? 0 : 1)
  const ticks = niceTicks(lowest, highest, 5)
  const min = ticks[0] ?? 0
  const max = ticks[ticks.length - 1] ?? 1
  const span = max - min || 1
  const formatTick = (value: number) => formatCompactColumnValue(value, activeMeasure, data.columnFormats)

  setFont(pdf, 6)
  const yLabelW = Math.max(8, ...ticks.map((tick) => pdf.getTextWidth(formatTick(tick)) + 2.5))
  const plotX = body.x + yLabelW
  const plotW = body.w - yLabelW - 2
  const plotTop = top + 1.5
  const plotH = body.y + body.h - LINE_X_AXIS_H - plotTop
  const toX = (index: number) => plotX + (rows.length <= 1 ? plotW / 2 : (index / (rows.length - 1)) * plotW)
  const toY = (value: number) => plotTop + plotH - ((value - min) / span) * plotH

  // Horizontal grid and Y ticks.
  setDrawColor(pdf, COLOR.grid)
  pdf.setLineWidth(0.2)
  setTextColor(pdf, COLOR.muted)
  ticks.forEach((tick) => {
    const y = toY(tick)
    pdf.line(plotX, y, plotX + plotW, y)
    pdf.text(formatTick(tick), plotX - 2, y + 1, { align: 'right' })
  })
  setDrawColor(pdf, COLOR.axisLine)
  pdf.line(plotX, plotTop + plotH, plotX + plotW, plotTop + plotH)

  // X labels: short dates, first and last always kept, the rest only where they do not collide.
  setFont(pdf, 6)
  setTextColor(pdf, COLOR.muted)
  const xLabels = rows.map((row) => formatShortDateLabel(row['label'] ?? null, granularity))
  const placed: Array<{ left: number; right: number }> = []
  const tryPlace = (index: number, force: boolean) => {
    const text = xLabels[index] ?? ''
    if (!text) return
    const width = pdf.getTextWidth(text)
    const center = toX(index)
    const left = Math.max(plotX - yLabelW + 1, Math.min(center - width / 2, plotX + plotW - width))
    const box = { left: left - 1.6, right: left + width + 1.6 }
    if (!force && placed.some((other) => box.left < other.right && box.right > other.left)) return
    placed.push(box)
    pdf.text(text, left, plotTop + plotH + 4.2)
  }
  const lastIndex = rows.length - 1
  tryPlace(0, true)
  if (lastIndex > 0) tryPlace(lastIndex, true)
  const step = Math.max(1, Math.ceil(rows.length / 8))
  for (let index = step; index < lastIndex; index += step) tryPlace(index, false)

  const baselineY = toY(Math.max(min, Math.min(0, max)))
  const pointsFor = (key: string): Point[] => rows.flatMap((row, index) => {
    const value = row[key]
    return typeof value === 'number' && Number.isFinite(value) ? [{ x: toX(index), y: toY(value) }] : []
  })
  // Hollow dots as on screen, sized to the point spacing so dense series stay readable.
  const dotRadius = Math.max(0.25, Math.min(0.7, plotW / Math.max(rows.length, 1) / 4.5))

  currentKeys.forEach((key) => {
    const color = hexToRgb(getLineSeriesColor(key, seriesKeys))
    const points = pointsFor(key)
    if (points.length === 0) return
    drawLineArea(pdf, points, baselineY, { x: plotX, w: plotW }, color)
    drawSmoothLine(pdf, points, color, 0.6)
    drawLineDots(pdf, points, color, dotRadius)
  })
  previousKeys.forEach((key) => {
    const color = hexToRgb(getLineSeriesColor(key, seriesKeys))
    const points = pointsFor(key)
    if (points.length === 0) return
    pdf.setLineDashPattern([1.3, 1], 0)
    drawSmoothLine(pdf, points, color, 0.5)
    pdf.setLineDashPattern([], 0)
    drawLineDots(pdf, points, color, dotRadius * 0.85)
  })
}

function traceSmoothPath(pdf: jsPDF, points: Point[]) {
  const [first] = points
  if (!first) return
  pdf.moveTo(first.x, first.y)
  monotoneCurveSegments(points).forEach((segment) => {
    pdf.curveTo(segment.c1.x, segment.c1.y, segment.c2.x, segment.c2.y, segment.to.x, segment.to.y)
  })
}

function drawSmoothLine(pdf: jsPDF, points: Point[], color: Rgb, width: number) {
  if (points.length < 2) return
  setDrawColor(pdf, color)
  pdf.setLineWidth(width)
  pdf.setLineJoin('round')
  pdf.setLineCap('round')
  traceSmoothPath(pdf, points)
  pdf.stroke()
  pdf.setLineCap('butt')
  pdf.setLineJoin('miter')
  pdf.setLineWidth(0.2)
}

function drawLineDots(pdf: jsPDF, points: Point[], color: Rgb, radius: number) {
  setFillColor(pdf, WHITE)
  setDrawColor(pdf, color)
  pdf.setLineWidth(0.35)
  points.forEach((point) => pdf.circle(point.x, point.y, radius, 'FD'))
  pdf.setLineWidth(0.2)
}

/** Area under the line with a vertical fade from 22% to 0% (buildLineGradientStops), drawn as bands clipped to the area. */
function drawLineArea(pdf: jsPDF, points: Point[], baselineY: number, plot: { x: number; w: number }, color: Rgb) {
  const first = points[0]
  const last = points[points.length - 1]
  if (!first || !last || points.length < 2) return
  const topY = Math.min(...points.map((point) => point.y), baselineY)
  const bottomY = Math.max(...points.map((point) => point.y), baselineY)
  if (bottomY - topY < 0.2) return

  pdf.saveGraphicsState()
  pdf.moveTo(first.x, baselineY)
  pdf.lineTo(first.x, first.y)
  monotoneCurveSegments(points).forEach((segment) => {
    pdf.curveTo(segment.c1.x, segment.c1.y, segment.c2.x, segment.c2.y, segment.to.x, segment.to.y)
  })
  pdf.lineTo(last.x, baselineY)
  pdf.close()
  pdf.clip()
  pdf.discardPath()

  const bands = 28
  const bandH = (bottomY - topY) / bands
  for (let index = 0; index < bands; index += 1) {
    const t = (index + 0.5) / bands
    setFillColor(pdf, blendRgb(color, WHITE, 0.22 * (1 - t)))
    pdf.rect(plot.x - 1, topY + index * bandH, plot.w + 2, bandH + 0.08, 'F')
  }
  pdf.restoreGraphicsState()
}

// ---------------------------------------------------------------------------
// Donut (DonutViz.tsx)

const DONUT_LEGEND_ROW_H = 7.4
const DONUT_LEGEND_GAP = 2

function buildDonutModel(data: AgentBuilderReportData) {
  const { dimensionCols, measureCols } = classifyColumns(data.columns, data.rows)
  const activeMeasure = measureCols[0] ?? data.chartData[0]?.series?.[0]?.name ?? 'Value'
  const dimension = data.groupByDimension ?? pickLabelColumn(dimensionCols, data.rows)
  const column = dimension ? findDrilldownColumn(data.columns, dimension) ?? dimension : null
  const derived = column ? deriveChartDataForDimension(data.columns, data.rows, column) : []
  return { activeMeasure, dimension, points: derived.length > 0 ? derived : data.chartData }
}

function getDonutLegendColumns(width: number) {
  return width >= 90 ? 2 : 1
}

function getDonutRingHeight(width: number) {
  return Math.min(50, width * 0.62)
}

function measureDonut(data: AgentBuilderReportData, width: number) {
  const { points } = buildDonutModel(data)
  if (points.length === 0) return 12
  const legendRows = Math.ceil(points.length / getDonutLegendColumns(width))
  return getDonutRingHeight(width) + 4 + legendRows * DONUT_LEGEND_ROW_H + (legendRows - 1) * DONUT_LEGEND_GAP
}

function drawDonut(pdf: jsPDF, body: Rect, data: AgentBuilderReportData) {
  const { activeMeasure, dimension, points } = buildDonutModel(data)
  if (points.length === 0) {
    drawNoData(pdf, body, 'No chart data available.')
    return
  }
  const colorAt = (index: number) => hexToRgb(DONUT_COLORS[index % DONUT_COLORS.length] ?? '#00c8b9')

  // Ring: inner/outer radius 72/112 as on screen, 2° between slices.
  const ringH = getDonutRingHeight(body.w)
  const outer = ringH / 2 - 1
  const inner = outer * (72 / 112)
  const cx = body.x + body.w / 2
  const cy = body.y + ringH / 2
  donutSliceAngles(points.map((point) => point.value)).forEach((slice, index) => {
    if (!slice) return
    fillPolygon(pdf, donutSlicePolygon(cx, cy, inner, outer, slice.start, slice.end), colorAt(index))
  })

  // Legend: every entry, in rounded boxes with the full value on the right.
  const cols = getDonutLegendColumns(body.w)
  const boxW = (body.w - DONUT_LEGEND_GAP * (cols - 1)) / cols
  const legendTop = body.y + ringH + 4
  points.forEach((point, index) => {
    const x = body.x + (index % cols) * (boxW + DONUT_LEGEND_GAP)
    const y = legendTop + Math.floor(index / cols) * (DONUT_LEGEND_ROW_H + DONUT_LEGEND_GAP)
    setFillColor(pdf, COLOR.legendBoxFill)
    setDrawColor(pdf, COLOR.legendBoxBorder)
    roundedRect(pdf, { x, y, w: boxW, h: DONUT_LEGEND_ROW_H }, 1.6, 'FD')
    setFillColor(pdf, colorAt(index))
    pdf.circle(x + 3.2, y + DONUT_LEGEND_ROW_H / 2, 0.95, 'F')

    const value = formatColumnValue(point.value, activeMeasure, data.columnFormats)
    setFont(pdf, 7, 'bold')
    setTextColor(pdf, COLOR.heading)
    const valueW = pdf.getTextWidth(value)
    pdf.text(value, x + boxW - 3, y + DONUT_LEGEND_ROW_H / 2 + 1, { align: 'right' })
    setFont(pdf, 7)
    setTextColor(pdf, COLOR.gray700)
    pdf.text(fitText(pdf, formatDimensionLabel(point.label, dimension, data.columnFormats), boxW - valueW - 10), x + 5.4, y + DONUT_LEGEND_ROW_H / 2 + 1)
  })
}

// ---------------------------------------------------------------------------
// Gauge (GaugeViz.tsx)

const GAUGE_VIEW_W = GAUGE_DIAL.viewWidth + GAUGE_DIAL.edgePadding * 2
const GAUGE_MAX_W = 105
const GAUGE_LEGEND_H = 13

function getGaugeDialWidth(width: number) {
  return Math.min(width, GAUGE_MAX_W)
}

function measureGauge(width: number) {
  const scale = getGaugeDialWidth(width) / GAUGE_VIEW_W
  return GAUGE_DIAL.viewHeight * scale + 3 + GAUGE_LEGEND_H
}

function drawGauge(pdf: jsPDF, body: Rect, widget: DashboardWidget, data: AgentBuilderReportData) {
  const values = resolveGaugeValues(data, widget)
  const { actual, target, ceiling, actualFormatKey, targetFormatKey, columnFormats } = values
  if (actual == null) {
    drawNoData(pdf, body, 'Gauge requires at least one numeric measure.')
    return
  }

  const progress = resolveGaugeProgress(values)
  const dialW = getGaugeDialWidth(body.w)
  const s = dialW / GAUGE_VIEW_W
  // viewBox x runs from -edgePadding; map dial units to page mm.
  const originX = body.x + (body.w - dialW) / 2 + GAUGE_DIAL.edgePadding * s
  const cx = originX + (GAUGE_DIAL.viewWidth / 2) * s
  const cy = body.y + GAUGE_DIAL.baselineY * s
  const arcR = GAUGE_DIAL.arcRadius * s
  const band = GAUGE_DIAL.bandWidth * s

  const actualPct = Math.max(0, Math.min(actual, ceiling)) / ceiling
  const targetAngle = target != null && target > 0 ? (Math.min(target, ceiling) / ceiling) * 180 : null

  strokePolyline(pdf, dialArcPoints(cx, cy, arcR, 0, 180), hexToRgb(GAUGE_COLORS.track), band, 'round')

  if (actualPct > 0) {
    // Horizontal gradient across the progress arc's own width, as on screen; drawn as short segments.
    const from = hexToRgb(GAUGE_PROGRESS_GRADIENT.from)
    const to = hexToRgb(GAUGE_PROGRESS_GRADIENT.to)
    const endAngle = actualPct * 180
    const arcPoints = dialArcPoints(cx, cy, arcR, 0, endAngle, 1.5)
    const minX = Math.min(...arcPoints.map((point) => point.x)) - band / 2
    const maxX = Math.max(...arcPoints.map((point) => point.x)) + band / 2
    const colorAtX = (x: number) => mixRgb(from, to, (x - minX) / Math.max(0.01, maxX - minX))
    const start = arcPoints[0]
    const end = arcPoints[arcPoints.length - 1]
    if (start) {
      setFillColor(pdf, colorAtX(start.x - band / 2))
      pdf.circle(start.x, start.y, band / 2, 'F')
    }
    if (end) {
      setFillColor(pdf, colorAtX(end.x + band / 4))
      pdf.circle(end.x, end.y, band / 2, 'F')
    }
    // Each segment reaches one point past its end, so neighbours overlap and no hairline gaps show.
    for (let index = 1; index < arcPoints.length; index += 1) {
      const a = arcPoints[index - 1]
      const b = arcPoints[index]
      const reach = arcPoints[Math.min(index + 1, arcPoints.length - 1)]
      if (!a || !b || !reach) continue
      strokePolyline(pdf, [a, b, reach], colorAtX((a.x + b.x) / 2), band, 'butt')
    }
  }

  // Text sizes follow the SVG's (viewBox units × scale, converted mm → pt).
  const pt = (units: number) => units * s * 2.835

  if (targetAngle != null) {
    const tickInner = dialPoint(cx, cy, GAUGE_DIAL.tickInnerRadius * s, targetAngle)
    const tickOuter = dialPoint(cx, cy, GAUGE_DIAL.tickOuterRadius * s, targetAngle)
    const labelPoint = dialPoint(cx, cy, GAUGE_DIAL.tickLabelRadius * s, targetAngle)
    strokePolyline(pdf, [tickInner, tickOuter], hexToRgb(GAUGE_COLORS.tick), 2.5 * s, 'round')
    const anchor = targetAngle >= 75 && targetAngle <= 105 ? 'center' : targetAngle < 75 ? 'right' : 'left'
    setFont(pdf, pt(11))
    setTextColor(pdf, hexToRgb(GAUGE_COLORS.targetLabel))
    pdf.text(
      `Target ${formatCompactColumnValue(target ?? 0, targetFormatKey, columnFormats)}`,
      labelPoint.x,
      labelPoint.y + (anchor === 'center' ? -4 : 4) * s,
      { align: anchor },
    )
  }

  setFont(pdf, pt(11))
  setTextColor(pdf, hexToRgb(GAUGE_COLORS.scale))
  pdf.text(formatCompactColumnValue(0, actualFormatKey, columnFormats), cx - arcR, cy + 26 * s, { align: 'center' })
  pdf.text(formatCompactColumnValue(ceiling, actualFormatKey, columnFormats), cx + arcR, cy + 26 * s, { align: 'center' })

  setFont(pdf, pt(34), 'bold')
  setTextColor(pdf, hexToRgb(GAUGE_COLORS.headline))
  pdf.text(formatCompactColumnValue(actual, actualFormatKey, columnFormats), cx, cy - 30 * s, { align: 'center' })
  if (progress) {
    setFont(pdf, pt(12), 'bold')
    setTextColor(pdf, hexToRgb(progress.status !== 'below' ? GAUGE_COLORS.met : GAUGE_COLORS.muted))
    pdf.text(`${progress.percentOfTarget}% of target`, cx, cy - 9 * s, { align: 'center' })
  }

  // Footer legend: exact figures under a divider, label above value.
  const legend = buildGaugeLegend(values)
  const legendTop = body.y + GAUGE_DIAL.viewHeight * s + 3
  setDrawColor(pdf, hexToRgb(GAUGE_COLORS.divider))
  pdf.line(body.x, legendTop, body.x + body.w, legendTop)
  const columnWidth = body.w / Math.max(1, legend.length)
  legend.forEach((entry, index) => {
    const x = body.x + columnWidth * index
    setFont(pdf, 6.6)
    setTextColor(pdf, COLOR.gray500)
    pdf.text(fitText(pdf, entry.label, columnWidth - 2), x, legendTop + 5)
    setFont(pdf, 8, 'bold')
    setTextColor(pdf, entry.tone ? hexToRgb(GAUGE_TONES[entry.tone].value) : hexToRgb(GAUGE_COLORS.headline))
    pdf.text(fitText(pdf, entry.value, columnWidth - 2), x, legendTop + 10)
  })
}

// ---------------------------------------------------------------------------
// Visual widget measuring and drawing

function measureVisualBody(measured: Omit<MeasuredWidget, 'height' | 'bodyHeight'>, bodyWidth: number) {
  const { widget, data } = measured
  switch (widget.type) {
    case 'kpi':
      // Like the screen, a KPI widget without cards shows its data as a bar chart.
      if (data.kpiCards.length === 0) return data.chartData.length > 0 ? measureBarChart(data, false) : 12
      return measureKpis(data, bodyWidth)
    case 'gauge':
      return measureGauge(bodyWidth)
    case 'groupedBar':
      return measureBarChart(data, true, widget.orientation)
    case 'line':
      return measureLineChart(data, resolveTrendGranularity(measured))
    case 'donut':
      return measureDonut(data, bodyWidth)
    case 'bar':
    default:
      return measureBarChart(data, false, widget.orientation)
  }
}

function drawVisualBody(pdf: jsPDF, measured: MeasuredWidget, body: Rect) {
  const { widget, data } = measured
  switch (widget.type) {
    case 'kpi':
      if (data.kpiCards.length === 0) {
        if (data.chartData.length > 0) drawBarChart(pdf, body, data, false)
        else drawNoData(pdf, body, 'No KPI data available.')
        return
      }
      drawKpis(pdf, body, data)
      return
    case 'gauge':
      drawGauge(pdf, body, widget, data)
      return
    case 'groupedBar':
      drawBarChart(pdf, body, data, true, widget.orientation)
      return
    case 'line':
      drawLineChart(pdf, body, data, resolveTrendGranularity(measured))
      return
    case 'donut':
      drawDonut(pdf, body, data)
      return
    case 'bar':
    default:
      drawBarChart(pdf, body, data, false, widget.orientation)
  }
}

function measureWidgets(pdf: jsPDF, snapshot: DashboardPdfSnapshot): MeasuredWidget[] {
  const contentW = getContentWidth(pdf)

  return snapshot.dashboard.widgets.flatMap((widget): MeasuredWidget[] => {
    if (widget.type === 'insight') return []
    const data = toWidgetData(widget, snapshot)
    if (!data) return []

    // A gauge shows the period it covers where other widgets show their description, and its gap-to-target badge.
    const headerWidget = widget.type === 'gauge'
      ? { ...widget, description: describeDateRangeSubtitle(snapshot.runtimeState.dateRange, snapshot.datePresetOptions) ?? '' }
      : widget
    const badge = widget.type === 'gauge' ? resolveGaugeBadge(resolveGaugeValues(data, widget)) : null
    const presentation = snapshot.widgetPresentation?.[widget.id] ?? {}
    const isTable = isTableWidget(headerWidget)
    const widthPercent = getWidgetWidthPercent(headerWidget)
    const base = { widget: headerWidget, data, presentation, badge, widthPercent, isTable }
    if (isTable) return [{ ...base, height: 0, bodyHeight: 0 }]

    const width = getWidgetBoxWidth(contentW, widthPercent)
    const layout = layoutCardHeader(pdf, headerWidget.title, headerWidget.description, width, badge)
    const bodyHeight = Math.min(
      measureVisualBody(base, width - CARD_PAD_X * 2),
      getVisualBodyMaxHeight(pdf, layout.bodyOffset),
    )
    return [{ ...base, bodyHeight, height: layout.bodyOffset + bodyHeight + CARD_BODY_BOTTOM }]
  })
}

function drawVisualWidget(pdf: jsPDF, measured: MeasuredWidget, rect: Rect) {
  const layout = layoutCardHeader(pdf, measured.widget.title, measured.widget.description, rect.w, measured.badge)
  const bodyY = drawCardFrame(pdf, rect, layout)
  drawVisualBody(pdf, measured, {
    x: rect.x + CARD_PAD_X,
    y: bodyY,
    w: rect.w - CARD_PAD_X * 2,
    h: Math.max(8, measured.bodyHeight),
  })
}

// ---------------------------------------------------------------------------
// Tables (Viz.tsx TableViz / ExpandableTable / DataBarTableViz / PivotTableViz)

type TableColumnSpec = {
  label: string
  align: 'left' | 'right'
  /** Minimum width, e.g. room for an in-cell bar. */
  minWidth?: number
}

type TableCell = {
  text: string
  color: Rgb
  bold?: boolean
  bar?: { percent: number; color: Rgb }
}

type TableRow =
  | { kind: 'row'; depth: number; expanded: boolean; cells: TableCell[] }
  | { kind: 'message'; depth: number; text: string }

export type PdfTableModel = {
  columns: TableColumnSpec[]
  rows: TableRow[]
  /** Deepest drilldown level; when above 0, first-column content is indented by level with a chevron on expanded rows. */
  maxDepth: number
  shownColumnCount: number
  totalColumnCount: number
}

const EMPTY_CELL: TableCell = { text: '', color: COLOR.ink }

function emptyNumericCell(): TableCell {
  return { text: '–', color: COLOR.emptyDash }
}

function numericCell(value: number, column: string, data: AgentBuilderReportData, bold = false): TableCell {
  return { text: formatColumnValue(value, column, data.columnFormats), color: value < 0 ? COLOR.negative : COLOR.ink, bold }
}

/** Plain (non-bar) cell: empty numeric cells show a grey en dash; everything else keeps formatTableCellValue. */
function plainCell(value: unknown, column: string, data: AgentBuilderReportData, numeric: boolean): TableCell {
  if (numeric && isEmptyCellValue(value)) return emptyNumericCell()
  const text = formatTableCellValue(value, column, data.columnFormats)
  if (!numeric) return { text, color: COLOR.gray700 }
  return { text, color: typeof value === 'number' && value < 0 ? COLOR.negative : COLOR.ink }
}

function barCell(value: number, max: number, colorIndex: number, column: string, data: AgentBuilderReportData): TableCell {
  const negative = value < 0
  return {
    text: formatColumnValue(value, column, data.columnFormats),
    color: negative ? COLOR.negative : COLOR.ink,
    bar: { percent: inCellBarPercent(value, max), color: negative ? COLOR.negative : hexToRgb(tableBarColor(colorIndex)) },
  }
}

/** Flat table (table / dataBarTable): every row, plus expanded drilldown rows under their parents. */
function buildFlatTableModel(data: AgentBuilderReportData, bars: boolean, expandedRows: Record<string, ExpandedTableResult>): PdfTableModel {
  const shown = data.columns.slice(0, MAX_TABLE_COLUMNS)
  const numericColumns = resolveNumericColumns(data)
  const barColumns = data.columns.filter((column) => numericColumns.has(column))
  // One scale per column over every top-level row, so bars compare across "(continued)" pages. Child rows clamp at 100%.
  const columnBarMax = bars
    ? buildColumnBarMax(data.columns, data.rows, (row, column, columnIndex) => resolveExpandableCellValue(row, column, columnIndex, data.columns))
    : {}
  const rows: TableRow[] = []
  let maxDepth = 0

  const visit = (sourceRows: Record<string, unknown>[], sourceColumns: string[], parentKey: string, depth: number) => {
    sourceRows.forEach((row) => {
      const rowKey = buildExpansionRowKey(row, sourceColumns, parentKey)
      const expanded = expandedRows[rowKey]
      maxDepth = Math.max(maxDepth, depth)
      const cells = shown.map((column): TableCell => {
        const columnIndex = data.columns.indexOf(column)
        const value = resolveExpandableCellValue(row, column, columnIndex, sourceColumns)
        const numeric = numericColumns.has(column)
        if (bars && typeof value === 'number') {
          return barCell(value, columnBarMax[column] ?? 1, Math.max(0, barColumns.indexOf(column)), column, data)
        }
        return plainCell(value, column, data, numeric)
      })
      rows.push({ kind: 'row', depth, expanded: Boolean(expanded), cells })
      if (!expanded) return
      if (expanded.rows.length === 0) {
        rows.push({ kind: 'message', depth: depth + 1, text: 'No child rows found.' })
        maxDepth = Math.max(maxDepth, depth + 1)
        return
      }
      visit(expanded.rows, expanded.columns, rowKey, depth + 1)
    })
  }
  visit(data.rows, data.columns, '', 0)

  return {
    columns: shown.map((column) => ({
      label: data.columnLabels?.[column] ?? formatFieldReferencesInText(column),
      align: numericColumns.has(column) ? 'right' : 'left',
      minWidth: bars && numericColumns.has(column) ? 22 : undefined,
    })),
    rows,
    maxDepth,
    shownColumnCount: shown.length,
    totalColumnCount: data.columns.length,
  }
}

/**
 * Pivot matrix (pivotTable, or dataBarTable with columnFields): row field, "{ColumnField}: {key}" columns,
 * then Total. Built over every row, so totals cover the whole dataset. Returns null when the data has no
 * row field, column field and measure, in which case the table prints flat (as on screen).
 */
function buildPivotTableModel(data: AgentBuilderReportData, bars: boolean, expandedRows: Record<string, ExpandedTableResult>): PdfTableModel | null {
  const { measureCols, dimensionCols } = classifyColumns(data.columns, data.rows)
  const rowLabel = bars ? dimensionCols[0] : dimensionCols[0] ?? data.columns[0]
  const columnLabel = dimensionCols[1]
  const valueColumn = measureCols[0]
  if (!rowLabel || !columnLabel || !valueColumn) return null

  const { rowKeys, columnKeys, matrix } = buildPivotMatrix(data.rows, rowLabel, columnLabel, valueColumn)
  // Row label and Total always print; the matrix columns fill the rest of the 12-column cap.
  const shownKeys = columnKeys.slice(0, Math.max(1, MAX_TABLE_COLUMNS - 2))
  const columnBarMax = new Map<string, number>()
  if (bars) {
    columnKeys.forEach((columnKey) => {
      const max = rowKeys.reduce((acc, rowKey) => Math.max(acc, Math.abs(matrix.get(rowKey)?.get(columnKey) ?? 0)), 0)
      columnBarMax.set(columnKey, max || 1)
    })
  }

  const rows: TableRow[] = []
  let maxDepth = 0
  const visitChildren = (expanded: ExpandedTableResult, parentKey: string, depth: number) => {
    maxDepth = Math.max(maxDepth, depth)
    if (expanded.rows.length === 0) {
      rows.push({ kind: 'message', depth, text: 'No child rows found.' })
      return
    }
    buildPivotChildRows(expanded, columnLabel, valueColumn, parentKey).forEach((child) => {
      const childKey = child.key
      const childExpanded = expandedRows[childKey]
      const label = child.label
      const childValueColumn = child.valueColumn ?? valueColumn
      rows.push({
        kind: 'row',
        depth,
        expanded: Boolean(childExpanded),
        cells: [
          { text: label === null || label === undefined ? '-' : formatFieldReferencesInText(formatTableCellString(String(label))), color: COLOR.gray700 },
          ...shownKeys.map((columnKey): TableCell => {
            if (!child.cells) return EMPTY_CELL
            const cell = child.cells.get(columnKey)
            return cell === undefined ? emptyNumericCell() : numericCell(cell, childValueColumn, data)
          }),
          child.total !== undefined ? numericCell(child.total, childValueColumn, data) : emptyNumericCell(),
        ],
      })
      if (childExpanded) visitChildren(childExpanded, childKey, depth + 1)
    })
  }

  rowKeys.forEach((rowKey) => {
    const rowMap = matrix.get(rowKey)
    const total = pivotRowTotal(matrix, rowKey, columnKeys)
    // Drill is keyed off the row-label dimension only (bar pivots have no drilldown on screen).
    const drillKey = buildExpansionRowKey({ [rowLabel]: rowKey }, [rowLabel], '')
    const expanded = bars ? undefined : expandedRows[drillKey]
    rows.push({
      kind: 'row',
      depth: 0,
      expanded: Boolean(expanded),
      cells: [
        { text: formatFieldReferencesInText(formatTableCellString(rowKey)), color: COLOR.gray700, bold: true },
        ...shownKeys.map((columnKey, columnIndex): TableCell => {
          const cell = rowMap?.get(columnKey)
          if (cell === undefined) return emptyNumericCell()
          return bars
            ? barCell(cell, columnBarMax.get(columnKey) ?? 1, columnIndex, valueColumn, data)
            : numericCell(cell, valueColumn, data)
        }),
        numericCell(total, valueColumn, data, true),
      ],
    })
    if (expanded) visitChildren(expanded, drillKey, 1)
  })

  return {
    columns: [
      { label: formatFieldReferencesInText(rowLabel), align: 'left' },
      ...shownKeys.map((columnKey): TableColumnSpec => ({
        label: `${formatFieldReferencesInText(columnLabel)}: ${formatFieldReferencesInText(formatTableCellString(columnKey))}`,
        align: 'right',
        minWidth: bars ? 20 : undefined,
      })),
      { label: 'Total', align: 'right' },
    ],
    rows,
    maxDepth,
    shownColumnCount: shownKeys.length + 2,
    totalColumnCount: columnKeys.length + 2,
  }
}

export function buildPdfTableModel(widget: DashboardWidget, data: AgentBuilderReportData, presentation: WidgetPresentationState = {}): PdfTableModel {
  const expandedRows = presentation.expandedRows ?? {}
  if (widget.type === 'pivotTable') {
    // Without a pivotable shape the screen falls back to a plain table (no bars, no drilldown).
    return buildPivotTableModel(data, false, expandedRows) ?? buildFlatTableModel(data, false, {})
  }
  if (widget.type === 'dataBarTable' && (widget.columnFields?.length ?? 0) > 0) {
    const pivot = buildPivotTableModel(data, true, {})
    if (pivot) return pivot
  }
  return buildFlatTableModel(data, widget.type === 'dataBarTable', expandedRows)
}

const TABLE_CELL_PAD = 2.2
const TABLE_HEADER_H = 7.4
/** Indent per drilldown level (the screen's 18px), and the room a row keeps before its first-column text for the chevron. */
const INDENT_STEP = 4
const CHEVRON_GAP = 3
/** Width the first column always keeps for its text, however deep the row is nested. */
const MIN_INDENTED_TEXT_W = 14

/** Left offset of a row's first-column content inside the cell padding: its level's indent, clamped so text keeps room. */
function getFirstColumnIndent(model: PdfTableModel, depth: number, columnWidth: number) {
  if (model.maxDepth <= 0) return { chevronX: 0, contentOffset: 0 }
  const maxIndent = Math.max(0, columnWidth - TABLE_CELL_PAD * 2 - CHEVRON_GAP - MIN_INDENTED_TEXT_W)
  const indent = Math.min(depth * INDENT_STEP, maxIndent)
  return { chevronX: indent + 1, contentOffset: indent + CHEVRON_GAP }
}

function getTableFontSizes(columnCount: number) {
  return columnCount > 8
    ? { header: 5.8, cell: 5.8, rowH: 6.4 }
    : { header: 6.4, cell: 6.6, rowH: 7 }
}

/** Column widths from header and content text, scaled to fill the table width. */
function resolveTableColumnWidths(pdf: jsPDF, model: PdfTableModel, width: number, sizes: ReturnType<typeof getTableFontSizes>) {
  const available = width
  const sample = model.rows.slice(0, 400)
  const natural = model.columns.map((column, columnIndex) => {
    setFont(pdf, sizes.header, 'bold')
    let widest = pdf.getTextWidth(column.label)
    sample.forEach((row) => {
      if (row.kind !== 'row') return
      const cell = row.cells[columnIndex]
      if (!cell?.text) return
      setFont(pdf, sizes.cell, cell.bold ? 'bold' : 'normal')
      const extra = columnIndex === 0 && model.maxDepth > 0 ? CHEVRON_GAP + row.depth * INDENT_STEP : 0
      widest = Math.max(widest, pdf.getTextWidth(cell.text) + extra + (cell.bar ? 4 : 0))
    })
    return Math.max(column.minWidth ?? 0, Math.min(widest, 70) + TABLE_CELL_PAD * 2)
  })
  // When space is short, bar columns give way first (down to 14mm); text columns keep up to 26mm for their labels.
  const minimum = natural.map((value, index) => Math.min(value, model.columns[index]?.minWidth ? 14 : 26))
  const total = natural.reduce((sum, value) => sum + value, 0)
  if (total <= available) {
    const scale = available / Math.max(total, 1)
    return { widths: natural.map((value) => value * scale) }
  }
  // Too wide: shrink each column toward its minimum in proportion to its slack.
  const minTotal = minimum.reduce((sum, value) => sum + value, 0)
  if (minTotal >= available) return { widths: minimum.map((value) => (value / minTotal) * available) }
  const slack = total - minTotal
  const ratio = (available - minTotal) / slack
  return { widths: natural.map((value, index) => (minimum[index] ?? 0) + (value - (minimum[index] ?? 0)) * ratio) }
}

function drawTableChunk(
  pdf: jsPDF,
  model: PdfTableModel,
  rows: TableRow[],
  x: number,
  y: number,
  width: number,
  layout: { widths: number[] },
  sizes: ReturnType<typeof getTableFontSizes>,
) {
  const height = TABLE_HEADER_H + Math.max(1, rows.length) * sizes.rowH
  const columnX: number[] = []
  layout.widths.reduce((left, columnWidth, index) => {
    columnX[index] = left
    return left + columnWidth
  }, x)

  // Header: tinted band with square bottom corners inside the rounded wrapper.
  setFillColor(pdf, COLOR.tableHead)
  roundedRect(pdf, { x, y, w: width, h: TABLE_HEADER_H }, 1.8, 'F')
  pdf.rect(x, y + TABLE_HEADER_H / 2, width, TABLE_HEADER_H / 2, 'F')
  setDrawColor(pdf, COLOR.line)
  pdf.line(x, y + TABLE_HEADER_H, x + width, y + TABLE_HEADER_H)
  setFont(pdf, sizes.header, 'bold')
  setTextColor(pdf, COLOR.label)
  model.columns.forEach((column, index) => {
    const left = columnX[index] ?? x
    const columnWidth = layout.widths[index] ?? 0
    const text = fitText(pdf, column.label, columnWidth - TABLE_CELL_PAD * 2)
    const textX = column.align === 'right' ? left + columnWidth - TABLE_CELL_PAD : left + TABLE_CELL_PAD
    pdf.text(text, textX, y + TABLE_HEADER_H / 2 + 1, { align: column.align })
  })

  const rowsTop = y + TABLE_HEADER_H
  if (rows.length === 0) {
    setFont(pdf, sizes.cell)
    setTextColor(pdf, COLOR.muted)
    pdf.text('No rows to display.', x + width / 2, rowsTop + sizes.rowH / 2 + 1, { align: 'center' })
  }

  rows.forEach((row, rowIndex) => {
    const rowY = rowsTop + rowIndex * sizes.rowH
    const midY = rowY + sizes.rowH / 2
    if (rowIndex < rows.length - 1) {
      setDrawColor(pdf, COLOR.rowDivider)
      pdf.line(x, rowY + sizes.rowH, x + width, rowY + sizes.rowH)
    }

    if (row.kind === 'message') {
      // Sits at its level, under the expanded parent's (absent) children.
      const { contentOffset } = getFirstColumnIndent(model, row.depth, layout.widths[0] ?? width)
      setFont(pdf, sizes.cell)
      setTextColor(pdf, COLOR.gray500)
      pdf.text(row.text, (columnX[0] ?? x) + TABLE_CELL_PAD + contentOffset, midY + 1)
      return
    }

    row.cells.forEach((cell, columnIndex) => {
      const column = model.columns[columnIndex]
      const left = columnX[columnIndex] ?? x
      const columnWidth = layout.widths[columnIndex] ?? 0
      if (!column) return
      let innerLeft = left + TABLE_CELL_PAD
      const innerRight = left + columnWidth - TABLE_CELL_PAD
      // Drilldown rows indent their first column by level, at any depth; expanded rows show a chevron.
      let barIndent = 0
      if (columnIndex === 0 && model.maxDepth > 0) {
        const { chevronX, contentOffset } = getFirstColumnIndent(model, row.depth, columnWidth)
        if (row.expanded) drawDownChevron(pdf, innerLeft + chevronX, midY, COLOR.gray500)
        innerLeft += contentOffset
        barIndent = contentOffset
      }

      if (cell.bar) {
        // In-cell bar (mockups `.cb`): rounded track, 35% fill from the left, value right-aligned on top.
        const barH = Math.min(4.6, sizes.rowH - 1.6)
        const track = { x: left + 1 + barIndent, y: midY - barH / 2, w: columnWidth - 2 - barIndent, h: barH }
        setFillColor(pdf, COLOR.cellBarTrack)
        roundedRect(pdf, track, 1, 'F')
        if (cell.bar.percent > 0) {
          setFillColor(pdf, blendRgb(cell.bar.color, COLOR.cellBarTrack, IN_CELL_BAR_OPACITY))
          roundedRect(pdf, { ...track, w: (track.w * cell.bar.percent) / 100 }, 1, 'F')
        }
        setFont(pdf, sizes.cell, 'normal')
        setTextColor(pdf, cell.color)
        pdf.text(fitText(pdf, cell.text, track.w - 3), track.x + track.w - 1.6, midY + 1, { align: 'right' })
        return
      }

      if (!cell.text) return
      setFont(pdf, sizes.cell, cell.bold ? 'bold' : 'normal')
      setTextColor(pdf, cell.color)
      const text = fitText(pdf, cell.text, innerRight - innerLeft)
      if (column.align === 'right') pdf.text(text, innerRight, midY + 1, { align: 'right' })
      else pdf.text(text, innerLeft, midY + 1)
    })
  })

  setDrawColor(pdf, COLOR.line)
  pdf.setLineWidth(0.25)
  roundedRect(pdf, { x, y, w: width, h: height }, 1.8, 'S')
  pdf.setLineWidth(0.2)
  return height
}

function getTableNotes(model: PdfTableModel, coverage: PdfRowCoverage | undefined) {
  const notes: string[] = []
  if (coverage && coverage.loaded < coverage.total) {
    notes.push(`Showing ${coverage.loaded.toLocaleString()} of ${coverage.total.toLocaleString()} rows.`)
  }
  if (model.totalColumnCount > model.shownColumnCount) {
    notes.push(`Showing ${model.shownColumnCount} of ${model.totalColumnCount} columns.`)
  }
  return notes.join('  ')
}

const TABLE_NOTE_H = 6

/** A table card per page: all rows, the header repeated, "(continued)" on later chunks, notes on the last. */
function drawTableWidget(cursor: PdfCursor, measured: MeasuredWidget, coverage: PdfRowCoverage | undefined) {
  const pdf = cursor.pdf
  const model = buildPdfTableModel(measured.widget, measured.data, measured.presentation)
  const sizes = getTableFontSizes(model.columns.length)
  const width = getContentWidth(pdf)
  const tableW = width - CARD_PAD_X * 2
  const columnLayout = resolveTableColumnWidths(pdf, model, tableW, sizes)
  const notes = getTableNotes(model, coverage)
  const noteH = notes ? TABLE_NOTE_H : 0
  let rowIndex = 0
  let continued = false

  for (;;) {
    const title = continued ? `${measured.widget.title} (continued)` : measured.widget.title
    const layout = layoutCardHeader(pdf, title, measured.widget.description, width)
    const fixedH = layout.bodyOffset + TABLE_HEADER_H + CARD_BODY_BOTTOM
    const available = getPageBottom(pdf) - cursor.y - fixedH
    const fitWithoutNote = Math.floor(Math.max(0, available) / sizes.rowH)
    const fitWithNote = Math.floor(Math.max(0, available - noteH) / sizes.rowH)
    const remaining = model.rows.length - rowIndex

    // Not even one row (or the empty-table message) fits below the header: start the card on a new page.
    if (fitWithNote < 1 && cursor.y > PAGE_TOP) {
      addPage(cursor)
      continue
    }

    const isLast = remaining <= Math.max(1, fitWithNote)
    const take = isLast ? remaining : Math.max(1, Math.min(fitWithoutNote, remaining - 1))
    const chunk = model.rows.slice(rowIndex, rowIndex + take)
    const tableH = TABLE_HEADER_H + Math.max(1, chunk.length) * sizes.rowH
    const rect = { x: PAGE_MARGIN, y: cursor.y, w: width, h: layout.bodyOffset + tableH + (isLast ? noteH : 0) + CARD_BODY_BOTTOM }

    const bodyY = drawCardFrame(pdf, rect, layout)
    drawTableChunk(pdf, model, chunk, rect.x + CARD_PAD_X, bodyY, tableW, columnLayout, sizes)
    if (isLast && notes) {
      setFont(pdf, 6.5)
      setTextColor(pdf, COLOR.muted)
      pdf.text(notes, rect.x + CARD_PAD_X, bodyY + tableH + 4.2)
    }

    cursor.y = rect.y + rect.h + GRID_GAP
    rowIndex += take
    if (isLast) break
    continued = true
    addPage(cursor)
  }
}

// ---------------------------------------------------------------------------
// Layout

function layoutVisualRow(cursor: PdfCursor, row: MeasuredWidget[]) {
  if (row.length === 0) return
  const pdf = cursor.pdf
  const contentW = getContentWidth(pdf)
  const rowH = Math.max(...row.map((widget) => widget.height))
  ensureSpace(cursor, rowH)

  // Cards in a row share the tallest card's height, like the viewer's stretched grid.
  let x = PAGE_MARGIN
  row.forEach((measured, index) => {
    const width = getWidgetBoxWidth(contentW, measured.widthPercent)
    drawVisualWidget(pdf, measured, { x, y: cursor.y, w: width, h: rowH })
    x += width + (index < row.length - 1 ? GRID_GAP : 0)
  })

  cursor.y += rowH + GRID_GAP
}

function layoutWidgets(cursor: PdfCursor, widgets: MeasuredWidget[], snapshot: DashboardPdfSnapshot) {
  let visualRow: MeasuredWidget[] = []
  let visualRowWidth = 0

  widgets.forEach((measured) => {
    if (measured.isTable) {
      layoutVisualRow(cursor, visualRow)
      visualRow = []
      visualRowWidth = 0
      drawTableWidget(cursor, measured, snapshot.rowCoverage?.[measured.widget.id])
      return
    }

    if (measured.widthPercent >= 100) {
      layoutVisualRow(cursor, visualRow)
      visualRow = []
      visualRowWidth = 0
      layoutVisualRow(cursor, [measured])
      return
    }

    const widgetWidth = getWidgetBoxWidth(getContentWidth(cursor.pdf), measured.widthPercent)
    const nextRowWidth = visualRow.length === 0
      ? widgetWidth
      : visualRowWidth + GRID_GAP + widgetWidth
    if (nextRowWidth > getContentWidth(cursor.pdf) + 0.1) {
      layoutVisualRow(cursor, visualRow)
      visualRow = []
      visualRowWidth = 0
    }

    visualRow.push(measured)
    visualRowWidth = visualRowWidth === 0 ? widgetWidth : visualRowWidth + GRID_GAP + widgetWidth
  })

  layoutVisualRow(cursor, visualRow)
}

/** Builds the PDF document (exported for tests; the app uses generateDashboardPdfBlob). */
export function buildDashboardPdf(snapshot: DashboardPdfSnapshot): jsPDF {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const cursor: PdfCursor = { pdf, y: PAGE_TOP }

  drawHeader(cursor, snapshot)
  drawDashboardContext(cursor, snapshot)
  layoutWidgets(cursor, measureWidgets(pdf, snapshot), snapshot)

  const pageCount = pdf.getNumberOfPages()
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page)
    setFont(pdf, 7)
    setTextColor(pdf, COLOR.muted)
    pdf.text(`Page ${page} of ${pageCount}`, getPageWidth(pdf) - PAGE_MARGIN, getPageHeight(pdf) - 7, { align: 'right' })
  }

  return pdf
}

export async function generateDashboardPdfBlob(snapshot: DashboardPdfSnapshot): Promise<Blob> {
  return buildDashboardPdf(snapshot).output('blob')
}

export function getDashboardPdfFilename(dashboard: DashboardDefinition) {
  const date = new Date().toISOString().slice(0, 10)
  const safe = (dashboard.title || 'Dashboard')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
  return `${safe || 'Dashboard'}-${date}.pdf`
}
